interface CacheItem {
  data: any;
  timestamp: number;
  ttl: number;
  key: string;
  compressed?: boolean;
}

interface CacheMetrics {
  hits: number;
  misses: number;
  size: number;
  keys: string[];
}

/**
 * Enhanced client-side cache 
 */
export class EnhancedClientCache {
  private cache: Map<string, CacheItem> = new Map();
  private metrics: CacheMetrics = { hits: 0, misses: 0, size: 0, keys: [] };
  private readonly maxSize: number;
  private readonly defaultTTL: number;
  private readonly enableCompression: boolean;

  constructor(
    maxSize: number = 100,
    defaultTTL: number = 5 * 60 * 1000, // 5 minutes
    enableCompression: boolean = false
  ) {
    this.maxSize = maxSize;
    this.defaultTTL = defaultTTL;
    this.enableCompression = enableCompression;

    // Cleanup expired items every minute
    setInterval(() => this.cleanup(), 60 * 1000);
  }

  /**
   * Store data in cache with optional TTL
   */
  set(key: string, data: any, ttl?: number): void {
    const item: CacheItem = {
      data: this.enableCompression ? this.compress(data) : data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
      key,
      compressed: this.enableCompression
    };

    // Remove oldest item if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const oldestKey = this.getOldestKey();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, item);
    this.updateMetrics();
  }

  /**
   * Retrieve data from cache if not expired
   */
  get(key: string): any | null {
    const item = this.cache.get(key);

    if (!item) {
      this.metrics.misses++;
      return null;
    }

    const isExpired = Date.now() - item.timestamp > item.ttl;
    if (isExpired) {
      this.cache.delete(key);
      this.metrics.misses++;
      this.updateMetrics();
      return null;
    }

    this.metrics.hits++;
    return item.compressed ? this.decompress(item.data) : item.data;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const isExpired = Date.now() - item.timestamp > item.ttl;
    if (isExpired) {
      this.cache.delete(key);
      this.updateMetrics();
      return false;
    }

    return true;
  }

  /**
   * Get or set pattern - retrieve from cache or compute and cache
   */
  async getOrSet<T>(
    key: string,
    computeFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }

    const computed = await computeFn();
    this.set(key, computed, ttl);
    return computed;
  }

  /**
   * Invalidate specific key or pattern
   */
  invalidate(keyOrPattern: string): void {
    if (keyOrPattern.includes('*')) {
      // Pattern matching
      const pattern = keyOrPattern.replace(/\*/g, '.*');
      const regex = new RegExp(pattern);

      const keysToDelete = Array.from(this.cache.keys()).filter(key =>
        regex.test(key)
      );

      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      // Exact key
      this.cache.delete(keyOrPattern);
    }

    this.updateMetrics();
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.metrics = { hits: 0, misses: 0, size: 0, keys: [] };
  }

  /**
   * Get cache statistics
   */
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  /**
   * Get cache hit ratio
   */
  getHitRatio(): number {
    const total = this.metrics.hits + this.metrics.misses;
    return total > 0 ? this.metrics.hits / total : 0;
  }

  /**
   * Prefetch data in background
   */
  async prefetch<T>(
    key: string,
    computeFn: () => Promise<T>,
    ttl?: number
  ): Promise<void> {
    // Don't prefetch if already cached and not close to expiry
    const item = this.cache.get(key);
    if (item) {
      const timeUntilExpiry = item.ttl - (Date.now() - item.timestamp);
      if (timeUntilExpiry > (item.ttl * 0.2)) { // More than 20% TTL remaining
        return;
      }
    }

    try {
      const data = await computeFn();
      this.set(key, data, ttl);
    } catch (error) {
      console.warn(`Prefetch failed for key ${key}:`, error);
    }
  }

  /**
   * Batch set multiple items
   */
  setMany(items: Array<{ key: string; data: any; ttl?: number }>): void {
    items.forEach(item => {
      this.set(item.key, item.data, item.ttl);
    });
  }

  /**
   * Batch get multiple items
   */
  getMany(keys: string[]): Record<string, any> {
    const result: Record<string, any> = {};
    keys.forEach(key => {
      const value = this.get(key);
      if (value !== null) {
        result[key] = value;
      }
    });
    return result;
  }

  /**
   * Export cache for persistence
   */
  export(): string {
    const exportData = Array.from(this.cache.entries()).map(([key, item]) => ({
      key,
      data: item.compressed ? item.data : item.data,
      timestamp: item.timestamp,
      ttl: item.ttl,
      compressed: item.compressed
    }));

    return JSON.stringify(exportData);
  }

  /**
   * Import cache from persistence
   */
  import(data: string): void {
    try {
      const importData = JSON.parse(data);
      this.cache.clear();

      importData.forEach((item: any) => {
        const cacheItem: CacheItem = {
          data: item.data,
          timestamp: item.timestamp,
          ttl: item.ttl,
          key: item.key,
          compressed: item.compressed
        };

        // Only import if not expired
        const isExpired = Date.now() - cacheItem.timestamp > cacheItem.ttl;
        if (!isExpired) {
          this.cache.set(item.key, cacheItem);
        }
      });

      this.updateMetrics();
    } catch (error) {
      console.error('Failed to import cache:', error);
    }
  }

  /**
   * Save cache to localStorage
   */
  persist(): void {
    try {
      const exported = this.export();
      localStorage.setItem('supply_chain_cache', exported);
    } catch (error) {
      console.warn('Failed to persist cache to localStorage:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  restore(): void {
    try {
      const stored = localStorage.getItem('supply_chain_cache');
      if (stored) {
        this.import(stored);
      }
    } catch (error) {
      console.warn('Failed to restore cache from localStorage:', error);
    }
  }

  /**
   * Cleanup expired items
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((item, key) => {
      if (now - item.timestamp > item.ttl) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => this.cache.delete(key));

    if (expiredKeys.length > 0) {
      this.updateMetrics();
    }
  }

  /**
   * Get oldest cache key for eviction
   */
  private getOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    this.cache.forEach((item, key) => {
      if (item.timestamp < oldestTimestamp) {
        oldestTimestamp = item.timestamp;
        oldestKey = key;
      }
    });

    return oldestKey;
  }

  /**
   * Update cache metrics
   */
  private updateMetrics(): void {
    this.metrics.size = this.cache.size;
    this.metrics.keys = Array.from(this.cache.keys());
  }

  /**
   * Simple compression (for demo - in production use proper compression)
   */
  private compress(data: any): string {
    return JSON.stringify(data);
  }

  /**
   * Simple decompression
   */
  private decompress(compressed: string): any {
    return JSON.parse(compressed);
  }
}

/**
 * Global cache instances for different data types
 */
export const rawDataCache = new EnhancedClientCache(50, 30 * 60 * 1000); // 30 minutes TTL for raw data
export const calculationCache = new EnhancedClientCache(100, 20 * 60 * 1000); // 20 minutes TTL for calculations
export const salesDataCache = new EnhancedClientCache(200, 30 * 60 * 1000); // 30 minutes TTL for sales data

/**
 * Cache version - increment this when formulas change to invalidate old caches
 */
const CACHE_VERSION = '2.2';

export function initializeCaches(): void {
  // Check cache version and clear if outdated
  const storedVersion = localStorage.getItem('supply_chain_cache_version');
  if (storedVersion !== CACHE_VERSION) {
    localStorage.removeItem('supply_chain_cache');
    localStorage.setItem('supply_chain_cache_version', CACHE_VERSION);
    rawDataCache.clear();
    calculationCache.clear();
    salesDataCache.clear();
  } else {
    // Restore from localStorage
    rawDataCache.restore();
    calculationCache.restore();
    salesDataCache.restore();
  }

  // Persist to localStorage every 5 minutes
  setInterval(() => {
    rawDataCache.persist();
    calculationCache.persist();
    salesDataCache.persist();
  }, 5 * 60 * 1000);
}

/**
 * Cache key generators for consistent naming
 */
export const CacheKeys = {
  rawInventory: (warehouse: string) => `raw_inventory_${warehouse}`,
  rawSales: (sku: string, warehouse: string) => `raw_sales_${sku}_${warehouse}`,
  rawCombined: (warehouse: string) => `raw_combined_${warehouse}`,
  calculatedMetrics: (warehouse: string) => `calculated_${warehouse}`,
  productMetrics: (product: string, warehouse: string) => `product_${product}_${warehouse}`,
  aggregatedData: (type: string, warehouse: string) => `aggregated_${type}_${warehouse}`,
};
